import { Box } from '@chakra-ui/react';
import { useMemo } from 'react';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import AppCrashDetailsView from '@/components/Templates/AppCrashDetailsView';
import { useGetCrashReportDetails } from '@/hooks/service-hooks/appCrashReport';
import usePermission from '@/hooks/usePermission';
import type { AppCrashReport } from '@/services/appCrashReport';
import { formatDateTime } from '@/utils';
import { getFriendlyErrorMessage } from '@/utils/friendlyErrorMessages';

interface AppCrashReportDetailsProps {
	id: string | null;
}

interface DetailColumn {
	data: Array<{
		label: string;
		value: string | number | undefined | null;
	}>;
}

const parseCrashReportDetailsForModal = (
	details: AppCrashReport | null,
	hasTechnicalAlertsPermission: boolean,
): DetailColumn[] => {
	if (!details) return [];

	return [
		{
			data: [
				{
					label: 'ID',
					value: details.errorId || '-',
				},
				{
					label: 'Type',
					value: details.type || '-',
				},
				{
					label: 'Module Name',
					value: details.moduleName || '-',
				},
				{
					label: 'User Name',
					value: details.userName || '-',
				},
				{
					label: 'Error Message',
					value: details.errorMessage ? (
						hasTechnicalAlertsPermission ? (
							details.errorMessage
						) : (
							<Box>
								<FaceliftText fontWeight="bold" mb={1}>
									{getFriendlyErrorMessage(details.errorMessage).title}
								</FaceliftText>
								<FaceliftText fontSize="sm" color="gray.600">
									{getFriendlyErrorMessage(details.errorMessage).message}
								</FaceliftText>
							</Box>
						)
					) : (
						'-'
					),
				},
			],
		},
		{
			data: [
				{
					label: 'Timestamp',
					value: details.createdAt
						? formatDateTime(details.createdAt)
						: details.timestamp || '-',
				},
				{
					label: 'Method',
					value: details.method || '-',
				},
				{
					label: 'Service Name',
					value: details.serviceName || '-',
				},
				{
					label: 'Environment',
					value: details.environment || '-',
				},
				{
					label: 'User Agent',
					value: details.userAgent || '-',
				},
			],
		},
	];
};

const AppCrashReportDetails = ({ id }: AppCrashReportDetailsProps) => {
	const { hasPermission } = usePermission();
	const hasTechnicalAlertsPermission = hasPermission([
		'TechnicalAlerts:Visualize',
	]);
	const { data: details, isLoading } = useGetCrashReportDetails(id, !!id);

	const transformedData = useMemo(
		() =>
			parseCrashReportDetailsForModal(
				details || null,
				hasTechnicalAlertsPermission,
			),
		[details, hasTechnicalAlertsPermission],
	);

	return (
		<Box>
			<Show when={isLoading}>
				<FaceliftDataLoader
					title="Loading crash report details..."
					description="Please wait while we fetch the crash report information."
				/>
			</Show>
			<Show when={!isLoading}>
				<AppCrashDetailsView
					data={transformedData}
					isLoading={false}
					gridConfig={{ base: '1fr', md: 'repeat(2, 1fr)' }}
				/>
			</Show>
		</Box>
	);
};

export default AppCrashReportDetails;
